import React, { useEffect, useState, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from "../components/ui/Input";
import { Skill } from '../types';
import { useSkillsApi } from '../apis/skills-apis';

interface SkillsSelectProps {
  selectedSkills: Skill[];
  onChange: (skills: Skill[]) => void;
  readOnly?: boolean;
}

export const SkillsSelect: React.FC<SkillsSelectProps> = ({
  selectedSkills,
  onChange,
  readOnly = false
}) => {
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { getSkills } = useSkillsApi();

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const getAllSkills = false;
        const skills = await getSkills(getAllSkills);
        setAllSkills(skills);
        setError(null);
      } catch (err) {
        setError('Failed to load skills');
        console.error('Error fetching skills:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSkills();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredSkills = searchQuery.length >= 1 ? allSkills.filter(skill => {
    const searchLower = searchQuery.toLowerCase();
    return (
      skill.skillName.toLowerCase().includes(searchLower) ||
      skill.skillDescription.toLowerCase().includes(searchLower) ||
      skill.skillTags?.some(tag => tag.toLowerCase().includes(searchLower))
    );
  }) : [];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsDropdownOpen(value.length >= 1);
  };

  const handleSkillToggle = (skill: Skill) => {
    if (readOnly) return;
    
    const newSelectedSkills = isSkillSelected(skill)
      ? selectedSkills.filter(s => s.skillId !== skill.skillId)
      : [...selectedSkills, skill];
    
    onChange(newSelectedSkills);
  };

  const isSkillSelected = (skill: Skill) => 
    selectedSkills.some(selected => selected.skillId === skill.skillId);

  const removeSkill = (skillToRemove: Skill) => {
    if (readOnly) return;
    onChange(selectedSkills.filter(skill => skill.skillId !== skillToRemove.skillId));
  };

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Selected Skills Display */}
      <div className="flex flex-wrap gap-2">
        {selectedSkills.map(skill => (
          <div
            key={skill.skillId}
            className="bg-blue-50 border border-blue-200 rounded-md px-3 py-1 flex items-center gap-2"
          >
            <span className="text-sm text-blue-700">{skill.skillName}</span>
            {!readOnly && (
              <button
                onClick={() => removeSkill(skill)}
                className="text-blue-400 hover:text-blue-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Search Input and Dropdown */}
      {!readOnly && (
        <div className="relative" ref={dropdownRef}>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Search skills..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-10"
              disabled={isLoading}
            />
          </div>

          {/* Dropdown Results */}
          {isDropdownOpen && searchQuery.length >= 1 && (
            <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500">Loading skills...</div>
              ) : filteredSkills.length > 0 ? (
                <div className="py-1">
                  {filteredSkills.map(skill => (
                    <div
                      key={skill.skillId}
                      onClick={() => {
                        handleSkillToggle(skill);
                        setSearchQuery('');
                        setIsDropdownOpen(false);
                        inputRef.current?.focus();
                      }}
                      className={`
                        px-4 py-2 cursor-pointer hover:bg-gray-50
                        ${isSkillSelected(skill) ? 'bg-blue-50' : ''}
                      `}
                    >
                      <div className="font-medium text-gray-900">{skill.skillName}</div>
                      <div className="text-sm text-gray-600">{skill.skillDescription}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  No skills found matching your search
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SkillsSelect;